import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { getActiveTenantId } from '@/lib/tenant';
import type {
  ImportJob,
  ImportJobListResponse,
  ImportMappingTarget,
  ImportPreview,
} from '@/lib/api/types';

const ACTIVE_STATUSES = new Set(['parsing', 'applying']);

function importsKey(catalogId: string) {
  return ['imports', getActiveTenantId(), catalogId] as const;
}

function importJobKey(jobId: string) {
  return ['import-job', getActiveTenantId(), jobId] as const;
}

export function useImports(catalogId: string) {
  return useQuery({
    queryKey: importsKey(catalogId),
    queryFn: () => apiFetch<ImportJobListResponse>(`/catalogs/${catalogId}/imports?limit=20`),
  });
}

export function useImportJob(jobId: string) {
  return useQuery({
    queryKey: importJobKey(jobId),
    queryFn: () => apiFetch<ImportJob>(`/imports/${jobId}`),
    refetchInterval: (query) =>
      query.state.data && ACTIVE_STATUSES.has(query.state.data.status) ? 2000 : false,
  });
}

export function useUploadImport(catalogId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiFetch<ImportJob>(`/catalogs/${catalogId}/imports`, {
        method: 'POST',
        body: formData,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: importsKey(catalogId) });
    },
  });
}

export function useImportPreview(jobId: string) {
  return useQuery({
    queryKey: ['import-preview', getActiveTenantId(), jobId],
    queryFn: () => apiFetch<ImportPreview>(`/imports/${jobId}/preview`),
  });
}

export function useConfirmMapping(catalogId: string, jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mapping: Record<string, ImportMappingTarget>) =>
      apiFetch<ImportJob>(`/imports/${jobId}/mapping`, { method: 'POST', body: { mapping } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: importJobKey(jobId) });
      void queryClient.invalidateQueries({ queryKey: importsKey(catalogId) });
    },
  });
}

export function useCancelImport(catalogId: string, jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<ImportJob>(`/imports/${jobId}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: importJobKey(jobId) });
      void queryClient.invalidateQueries({ queryKey: importsKey(catalogId) });
    },
  });
}

export function useRelaunchImport(catalogId: string, jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<ImportJob>(`/imports/${jobId}/relaunch`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: importJobKey(jobId) });
      void queryClient.invalidateQueries({ queryKey: importsKey(catalogId) });
    },
  });
}
